import { useCallback, useEffect, useRef, useState } from 'react';

const REQUEST_STATUSES = {
  idle: 'idle',
  loading: 'loading',
  success: 'success',
  error: 'error',
} as const;

type RequestStatus = (typeof REQUEST_STATUSES)[keyof typeof REQUEST_STATUSES];

export type UseAdministrationRequestOptions<TData> = {
  requestFn: () => Promise<TData>;
  initialData: TData;
  enabled?: boolean;
};

export type UseAdministrationRequestResult<TData, TError = unknown> = {
  data: TData;
  error: TError | null;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  status: RequestStatus;
  refetch: () => Promise<void>;
};

export function useAdministrationRequest<TData, TError = unknown>({
  requestFn,
  initialData,
  enabled = true,
}: UseAdministrationRequestOptions<TData>): UseAdministrationRequestResult<TData, TError> {
  const [data, setData] = useState<TData>(initialData);
  const [status, setStatus] = useState<RequestStatus>(enabled ? REQUEST_STATUSES.loading : REQUEST_STATUSES.idle);
  const [error, setError] = useState<TError | null>(null);
  const activeRequestId = useRef(0);
  const initialDataRef = useRef(initialData);

  const execute = useCallback(async () => {
    if (!enabled) {
      setStatus(REQUEST_STATUSES.idle);
      setError(null);
      setData(initialDataRef.current);
      return;
    }

    const requestId = activeRequestId.current + 1;
    activeRequestId.current = requestId;

    setStatus(REQUEST_STATUSES.loading);
    setError(null);

    try {
      const result = await requestFn();
      if (activeRequestId.current !== requestId) {
        return;
      }
      setData(result);
      setStatus(REQUEST_STATUSES.success);
    } catch (fetchError) {
      if (activeRequestId.current !== requestId) {
        return;
      }
      setData(initialDataRef.current);
      setStatus(REQUEST_STATUSES.error);
      setError(fetchError as TError);
    }
  }, [enabled, requestFn]);

  useEffect(() => {
    void execute();
  }, [execute]);

  const refetch = useCallback(async () => {
    await execute();
  }, [execute]);

  return {
    data,
    error,
    isLoading: status === REQUEST_STATUSES.loading,
    isError: status === REQUEST_STATUSES.error,
    isSuccess: status === REQUEST_STATUSES.success,
    status,
    refetch,
  };
}
